/**
 * The address book's type-ahead on a party field (#159, spec D8): the shared `BookTypeAhead`
 * (`BookCombobox.tsx`, which holds the ARIA pattern, the keys and the asking) bound to a party block.
 *
 * **What choosing does** is `partyFill.tsx`'s: the block's name, NIF and, where the block has one,
 * address. This field is the search, so its text is replaced without asking. Text in the block's
 * other fields that the choice would change is not, and the shared dialog asks first. A saved
 * shipper's DeCA recipients and «Enviar automáticamente» come with it (#219).
 */

import { type ComponentPropsWithRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { type AddressBookItem } from '../app/addressBook.ts'
import { BookTypeAhead, type OptionView } from './BookCombobox.tsx'
import {
  replacesOtherText,
  useConfirmPartyOverwrite,
  writeParty,
  type PartyField,
  type PartyValues,
} from './partyFill.tsx'
import { useToastIfAny } from './Toast.tsx'
import { MAX_DECA_RECIPIENTS } from '../deca/emailRecipients.ts'

/** A party reads as its name over its NIF and address, wherever it comes from. */
const viewOf = (option: AddressBookItem): OptionView => ({
  title: option.name,
  detail: [option.nif, option.address].filter(Boolean).join(' · '),
  fromDocuments: false,
})

export function PartyCombobox({
  party,
  input,
}: {
  /** The block this field belongs to, and which of its two searchable fields it is. */
  party: PartyField
  /** The field `BaseField` would otherwise have rendered, with its registration. */
  input: ComponentPropsWithRef<'input'>
}) {
  const { t } = useTranslation()
  const form = useFormContext()
  const toast = useToastIfAny()
  const overwrite = useConfirmPartyOverwrite()

  const choose = (option: AddressBookItem) => {
    // A party's name and NIF are never null; the type allows it for the book's other kinds (#221).
    const values: PartyValues = { name: option.name ?? '', nif: option.nif, address: option.address }
    const fill = () => {
      writeParty(form, party.columns, values)
      fillRecipients(option)
    }
    if (replacesOtherText(form, party.columns, values, party.columns[party.field])) {
      overwrite.ask(values.name, fill)
    } else {
      fill()
    }
  }

  /**
   * A saved entry's DeCA recipients and «Enviar automáticamente», where the block has them — the
   * shipper's (#219): the first 10, a DeCA's most, said when there were more. A counterparty from
   * the documents has none, and leaves what the form holds.
   */
  const fillRecipients = (option: AddressBookItem) => {
    const target = party.columns.recipients
    if (!target || option.source !== 'ENTRY') return
    form.setValue(target.list, option.decaRecipients.slice(0, MAX_DECA_RECIPIENTS), { shouldDirty: true })
    form.setValue(target.autoSend, option.decaAutoSend, { shouldDirty: true })
    if (option.decaRecipients.length > MAX_DECA_RECIPIENTS) {
      toast?.show(t('create.email.firstTen', { count: option.decaRecipients.length, name: option.name }), 'info')
    }
  }

  return (
    <BookTypeAhead kind="PARTY" input={input} view={viewOf} onChoose={choose}>
      {overwrite.dialog}
    </BookTypeAhead>
  )
}
